"use client";

import { useCallback, useMemo, useState } from "react";
import { PillGroup } from "@/components/tool-kit/primitives";
import {
  SourceCitationBuilder,
  type SourceField,
  type SourceValues,
} from "@/components/tool-kit/source-citation-builder";
import { useToolUi } from "@/components/tools/tool-ui";
import {
  CFR_FIELDS as C,
  cfrIsReady,
  federalRegisterIsReady,
  formatCfrRegulation,
  formatFederalRegisterRule,
  FEDERAL_REGISTER_FIELDS as R,
  type RegulationLabels,
} from "@/lib/tool-kit/legal-sources/regulation-citation-generator";

type Source = "cfr" | "fr";

/** English strings. A translated content file overrides these through `ui`. */
export const REGULATION_CITATION_GENERATOR_STRINGS = {
  formHeading: "Regulation details",
  resultsHeading: "Your citations",
  emptyPromptCfr:
    "Enter the C.F.R. title, the part or section, and the edition year to see your citations.",
  emptyPromptFr:
    "Enter the rule's name, Federal Register volume, first page and date to see your citations.",
  copy: "Copy",
  copied: "Copied!",
  tryExample: "Try an example",
  clear: "Clear",
  sourceLabel: "Where the regulation is published",
  sourceCfr: "Code of Federal Regulations (rule in force)",
  sourceFr: "Federal Register (new or proposed rule)",
  nameLabel: "Name of the regulation (optional for C.F.R.)",
  namePlaceholder: "Protection of Human Subjects",
  cfrTitleLabel: "C.F.R. title",
  cfrTitlePlaceholder: "45",
  unitLabel: "Citing a",
  unitSection: "Section (§)",
  unitPart: "Whole part (pt.)",
  locatorLabel: "Section or part number",
  locatorPlaceholder: "46.102",
  locatorHint: "46.102 is part 46, section 102. For a whole part, type 46.",
  yearLabel: "Year of the C.F.R. edition",
  yearPlaceholder: "2024",
  yearHint:
    "The year on the annual edition you used — not the year the rule was adopted.",
  agencyLabel: "Issuing agency (for MLA)",
  agencyPlaceholder: "Department of Health and Human Services",
  websiteLabel: "Website (for MLA)",
  websitePlaceholder: "GovInfo",
  urlLabel: "URL",
  urlPlaceholderCfr:
    "https://www.govinfo.gov/app/details/CFR-2024-title45-vol1/CFR-2024-title45-vol1-sec46-102",
  ruleNameLabel: "Title of the rule",
  ruleNamePlaceholder:
    "Defining and Delimiting the Exemptions for Executive, Administrative, Professional, Outside Sales and Computer Employees",
  shortTitleLabel: "Short title for in-text citations (optional)",
  shortTitlePlaceholder: "Defining and Delimiting",
  ruleTypeLabel: "Type of rule",
  ruleTypeFinal: "Final rule",
  ruleTypeProposed: "Proposed rule",
  volumeLabel: "Federal Register volume",
  volumePlaceholder: "80",
  issueLabel: "Issue number (for MLA)",
  issuePlaceholder: "128",
  pageLabel: "First page",
  pagePlaceholder: "38516",
  lastPageLabel: "Last page (for MLA)",
  lastPagePlaceholder: "38612",
  dateLabel: "Publication date",
  datePlaceholder: "YYYY-MM-DD",
  codTitleLabel: "To be codified in C.F.R. title (optional)",
  codTitlePlaceholder: "29",
  codUnitLabel: "Codified as a",
  codLocatorLabel: "Part or section number",
  codLocatorPlaceholder: "541",
  frAgencyPlaceholder: "Department of Labor, Wage and Hour Division",
  urlPlaceholderFr: "https://www.federalregister.gov/d/2015-15464",
  apaLabel: "APA 7th edition",
  mlaLabel: "MLA 9th edition",
  chicagoLabel: "Chicago 18th edition (notes)",
  referenceList: "Reference list",
  parenthetical: "In-text citation (parenthetical)",
  narrative: "In-text citation (narrative)",
  worksCited: "Works Cited",
  inText: "In-text citation",
  footnote: "Footnote",
  apaNote:
    "APA 7 (§11.7) follows legal form. Its manual abbreviates the Federal Register F.R. and writes page numbers without commas.",
  mlaNote:
    "MLA cites the copy you consulted. The Code of Federal Regulations stays in roman type; the Federal Register is italic, like a journal.",
  chicagoNote:
    "Chicago uses the Bluebook form (Fed. Reg., commas in five-digit pages) in notes only. Law reviews abbreviate the month (Sept., not September).",
  exampleCfr: {
    name: "Protection of Human Subjects",
    cfrTitle: "45",
    unit: "section",
    locator: "46.102",
    year: "2024",
    agency: "Department of Health and Human Services",
    website: "GovInfo",
    url: "https://www.govinfo.gov/app/details/CFR-2024-title45-vol1/CFR-2024-title45-vol1-sec46-102",
  },
  exampleFr: {
    name: "Defining and Delimiting the Exemptions for Executive, Administrative, Professional, Outside Sales and Computer Employees",
    shortTitle: "Defining and Delimiting",
    ruleType: "proposed",
    volume: "80",
    issue: "128",
    page: "38516",
    lastPage: "38612",
    date: "2015-07-06",
    codTitle: "29",
    codUnit: "part",
    codLocator: "541",
    agency: "Department of Labor, Wage and Hour Division",
    url: "https://www.federalregister.gov/d/2015-15464",
  },
} as const;

type Strings = typeof REGULATION_CITATION_GENERATOR_STRINGS;

function cfrFields(strings: Strings): SourceField[] {
  return [
    {
      id: C.name,
      kind: "text",
      label: strings.nameLabel,
      placeholder: strings.namePlaceholder,
      wide: true,
    },
    {
      id: C.title,
      kind: "text",
      label: strings.cfrTitleLabel,
      placeholder: strings.cfrTitlePlaceholder,
    },
    {
      id: C.unit,
      kind: "select",
      label: strings.unitLabel,
      options: [
        { value: "section", label: strings.unitSection },
        { value: "part", label: strings.unitPart },
      ],
    },
    {
      id: C.locator,
      kind: "text",
      label: strings.locatorLabel,
      placeholder: strings.locatorPlaceholder,
      hint: strings.locatorHint,
    },
    {
      id: C.year,
      kind: "text",
      label: strings.yearLabel,
      placeholder: strings.yearPlaceholder,
      hint: strings.yearHint,
    },
    {
      id: C.agency,
      kind: "text",
      label: strings.agencyLabel,
      placeholder: strings.agencyPlaceholder,
    },
    {
      id: C.website,
      kind: "text",
      label: strings.websiteLabel,
      placeholder: strings.websitePlaceholder,
    },
    {
      id: C.url,
      kind: "url",
      label: strings.urlLabel,
      placeholder: strings.urlPlaceholderCfr,
      wide: true,
    },
  ];
}

function frFields(strings: Strings): SourceField[] {
  return [
    {
      id: R.name,
      kind: "textarea",
      label: strings.ruleNameLabel,
      placeholder: strings.ruleNamePlaceholder,
    },
    {
      id: R.shortTitle,
      kind: "text",
      label: strings.shortTitleLabel,
      placeholder: strings.shortTitlePlaceholder,
    },
    {
      id: R.ruleType,
      kind: "select",
      label: strings.ruleTypeLabel,
      options: [
        { value: "final", label: strings.ruleTypeFinal },
        { value: "proposed", label: strings.ruleTypeProposed },
      ],
    },
    {
      id: R.volume,
      kind: "text",
      label: strings.volumeLabel,
      placeholder: strings.volumePlaceholder,
    },
    {
      id: R.issue,
      kind: "text",
      label: strings.issueLabel,
      placeholder: strings.issuePlaceholder,
    },
    {
      id: R.page,
      kind: "text",
      label: strings.pageLabel,
      placeholder: strings.pagePlaceholder,
    },
    {
      id: R.lastPage,
      kind: "text",
      label: strings.lastPageLabel,
      placeholder: strings.lastPagePlaceholder,
    },
    {
      id: R.date,
      kind: "date",
      label: strings.dateLabel,
      placeholder: strings.datePlaceholder,
    },
    {
      id: R.codTitle,
      kind: "text",
      label: strings.codTitleLabel,
      placeholder: strings.codTitlePlaceholder,
    },
    {
      id: R.codUnit,
      kind: "select",
      label: strings.codUnitLabel,
      options: [
        { value: "part", label: strings.unitPart },
        { value: "section", label: strings.unitSection },
      ],
    },
    {
      id: R.codLocator,
      kind: "text",
      label: strings.codLocatorLabel,
      placeholder: strings.codLocatorPlaceholder,
    },
    {
      id: R.agency,
      kind: "text",
      label: strings.agencyLabel,
      placeholder: strings.frAgencyPlaceholder,
    },
    {
      id: R.url,
      kind: "url",
      label: strings.urlLabel,
      placeholder: strings.urlPlaceholderFr,
      wide: true,
    },
  ];
}

/** The widget's strings, as the formatter's labels. */
export function regulationLabels(strings: Strings): RegulationLabels {
  return {
    apa: strings.apaLabel,
    mla: strings.mlaLabel,
    chicago: strings.chicagoLabel,
    referenceList: strings.referenceList,
    parenthetical: strings.parenthetical,
    narrative: strings.narrative,
    worksCited: strings.worksCited,
    inText: strings.inText,
    footnote: strings.footnote,
    apaNote: strings.apaNote,
    mlaNote: strings.mlaNote,
    chicagoNote: strings.chicagoNote,
  };
}

export default function RegulationCitationGeneratorWidget() {
  const strings = useToolUi(REGULATION_CITATION_GENERATOR_STRINGS);
  const [source, setSource] = useState<Source>("cfr");
  const fields = useMemo(
    () => (source === "cfr" ? cfrFields(strings) : frFields(strings)),
    [source, strings]
  );
  const labels = useMemo(() => regulationLabels(strings), [strings]);
  const format = useCallback(
    (values: SourceValues) =>
      source === "cfr"
        ? formatCfrRegulation(values, labels)
        : formatFederalRegisterRule(values, labels),
    [source, labels]
  );
  const builderStrings = useMemo(
    () => ({
      ...strings,
      emptyPrompt:
        source === "cfr" ? strings.emptyPromptCfr : strings.emptyPromptFr,
    }),
    [source, strings]
  );

  return (
    <SourceCitationBuilder
      example={source === "cfr" ? strings.exampleCfr : strings.exampleFr}
      fields={fields}
      format={format}
      intro={
        <PillGroup
          label={strings.sourceLabel}
          name="regulation-citation-source"
          onChange={setSource}
          options={[
            { value: "cfr", label: strings.sourceCfr },
            { value: "fr", label: strings.sourceFr },
          ]}
          value={source}
        />
      }
      isReady={source === "cfr" ? cfrIsReady : federalRegisterIsReady}
      strings={builderStrings}
    />
  );
}
